"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

interface ForgotPasswordFormData {
  email: string;
}

export default function ForgotPasswordPage() {
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: "",
  });

  const [errors, setErrors] = useState<Partial<ForgotPasswordFormData>>({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: keyof ForgotPasswordFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ForgotPasswordFormData> = {};

    if (!formData.email) {
      newErrors.email = "E-postadresse er påkrevd";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Ugyldig e-postformat";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Mock password reset - in real implementation, this would send reset email
      console.log("Password reset request for:", formData.email);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8 animate-fade-in">
            <Link href="/homepage" className="inline-flex items-center gap-3 group mb-6">
              <AppImage
                src="/assets/images/FullLogo_Transparent-1770223924492.png"
                alt="Oslo Bil Auto AS Logo"
                width={200}
                height={50}
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Success Message */}
          <div className="glass border border-border rounded-2xl p-8 shadow-soft animate-scale-in text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" className="text-success" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Sjekk e-posten din</h1>
            <p className="text-muted-foreground mb-6">
              Vi har sendt instruksjoner for tilbakestilling av passord til{" "}
              <span className="font-semibold text-foreground">{formData.email}</span>
            </p>
            <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                Hvis du ikke mottar e-posten innen noen minutter, sjekk søppelpostmappen din.
              </p>
            </div>
            <Link
              href="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-glow"
            >
              <Icon name="ArrowLeftIcon" size={20} />
              Tilbake til innlogging
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fade-in">
          <Link href="/homepage" className="inline-flex items-center gap-3 group mb-6">
            <AppImage
              src="/assets/images/FullLogo_Transparent-1770223924492.png"
              alt="Oslo Bil Auto AS Logo"
              width={200}
              height={50}
              className="h-12 w-auto object-contain"
            />
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-2">Glemt passord?</h1>
          <p className="text-muted-foreground">
            Ingen problem! Skriv inn e-postadressen din, så sender vi deg instruksjoner for tilbakestilling.
          </p>
        </div>

        {/* Forgot Password Form */}
        <div className="glass border border-border rounded-2xl p-8 shadow-soft animate-scale-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                E-postadresse *
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="din@epost.no"
                  className={`w-full px-4 py-3 pl-11 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                    errors.email ? "border-error" : "border-border"
                  }`}
                />
                <Icon
                  name="EnvelopeIcon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={20}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-glow flex items-center justify-center gap-2"
            >
              Send tilbakestillingsinstruksjoner
              <Icon name="PaperAirplaneIcon" size={20} />
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              href="/login"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Tilbake til innlogging
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}